import { nanoid } from "nanoid";
import { EmploymentBox } from "./employment";
import { OtherIncomeBox } from "./otherIncome";
import { RentalIncomeBox } from "./rentalIncome";
import PlusIcon from "../../assets/plusIconGreen.svg";
import { useEffect, useRef, useState } from "react";
import { SelfEmploymentBox } from "./selfEmployment";
import { convertToFormattedAmount } from "../../utils/convertAmount";
import { useIncomeStore } from "../../stores/employmentDetailsStore";
import { useUserMortgageProfileStore } from "../../stores/userProfileStore";

const EmploymentDetailsView = () => {
  const [showOptions, setShowOptions] = useState(true);
  const { userMortgageProfile } = useUserMortgageProfileStore();

  const {
    addedIncomes,
    incomeData,
    addIncome,
    removeIncome,
    updateIncomeData,
    setAddedIncomes,
  } = useIncomeStore();
  const processedIncomeIds = useRef(new Set<string>());
  useEffect(() => {
    const syncIncomeSources = () => {
      if (userMortgageProfile?.incomeSource) {
        setAddedIncomes([]);
        processedIncomeIds.current.clear();

        for (const [key, incomes] of Object.entries(
          userMortgageProfile.incomeSource
        )) {
          if (Array.isArray(incomes)) {
            for (const income of incomes) {
              const id = nanoid();
              processedIncomeIds.current.add(id);
              addIncome(key, id);

              for (const [field, value] of Object.entries(income)) {
                if (
                  value !== undefined &&
                  value !== null &&
                  !(typeof value === "number" && Number.isNaN(value))
                ) {
                  const isCurrencyInterface =
                    typeof value === "object" &&
                    value !== null &&
                    "amount" in value &&
                    "currency" in value;

                  if (key === "rentalIncome" && field === "propertyAddress") {
                    const { address1, address2, city, state, postalCode } =
                      value || {};
                    updateIncomeData(id, "address", address1 || "");
                    updateIncomeData(id, "address2", address2 || "");
                    updateIncomeData(id, "city", city || "");
                    updateIncomeData(id, "province", state || "");
                    updateIncomeData(id, "postalCode", postalCode || "");
                  } else if (
                    (key === "fullTime" || key === "partTime") &&
                    field === "startDate"
                  ) {
                    updateIncomeData(id, "startDate", value);
                  } else if (key === "otherIncome" && field === "startDate") {
                    updateIncomeData(id, "approximateStartDate", value);
                  } else {
                    updateIncomeData(
                      id,
                      field,
                      isCurrencyInterface
                        ? value.amount === 0
                          ? "0"
                          : convertToFormattedAmount(value.amount)
                        : value
                    );
                  }
                }
              }
            }
          }
        }
      }
    };

    syncIncomeSources();
  }, [userMortgageProfile, addIncome, updateIncomeData, setAddedIncomes]);

  const handleAddIncome = (key: string) => {
    const id = nanoid();
    addIncome(key, id);
    setShowOptions(false);
  };

  return (
    <div className="space-y-4 w-full flex flex-col">
      <div className="w-full">
        <div className="font-bold text-2xl w-full">
          Income & Employment Details
        </div>
        <div className="text-[#6B7280] w-full">
          To start, which of the following best describes your current
          employment situation? You can add more later.
        </div>
      </div>
      <hr className="bg-[#6B7280] w-full" />

      {addedIncomes.map(({ key, id }) => {
        const errors = incomeData[id]?.errors || {};
        switch (key) {
          case "fullTime":
          case "partTime": {
            const employmentType = key === "fullTime" ? "Full Time" : "Part Time";
            return (
              <div key={id} className="w-full">
                <EmploymentBox
                  label={employmentType}
                  onRemove={() => removeIncome(id)}
                  employerName={incomeData[id]?.employerName || ""}
                  setEmployerName={(value) =>
                    updateIncomeData(id, "employerName", value)
                  }
                  jobTitle={incomeData[id]?.jobTitle || ""}
                  setJobTitle={(value) =>
                    updateIncomeData(id, "jobTitle", value)
                  }
                  annualBasePay={incomeData[id]?.annualBasePay || ""}
                  setAnnualBasePay={(value) =>
                    updateIncomeData(id, "annualBasePay", value)
                  }
                  annualBonuses={incomeData[id]?.annualBonuses || ""}
                  setAnnualBonuses={(value) =>
                    updateIncomeData(id, "annualBonuses", value)
                  }
                  annualOvertime={incomeData[id]?.annualOvertime || ""}
                  setAnnualOvertime={(value) =>
                    updateIncomeData(id, "annualOvertime", value)
                  }
                  annualCommission={incomeData[id]?.annualCommission || ""}
                  setAnnualCommission={(value) =>
                    updateIncomeData(id, "annualCommission", value)
                  }
                  startDate={incomeData[id]?.startDate || null}
                  setStartDate={(value) =>
                    updateIncomeData(id, "startDate", value)
                  }
                  employerNameError={errors.employerName}
                  jobTitleError={errors.jobTitle}
                  annualBasePayError={errors.annualBasePay}
                  annualBonusesError={errors.annualBonuses}
                  annualOvertimeError={errors.annualOvertime}
                  annualCommissionError={errors.annualCommission}
                  startDateError={errors.startDate}
                />
              </div>
            );
          }
          case "selfEmployed":
            return (
              <div key={id} className="w-full">
                <SelfEmploymentBox
                  onRemove={() => removeIncome(id)}
                  businessName={incomeData[id]?.businessName || ""}
                  setBusinessName={(value) =>
                    updateIncomeData(id, "businessName", value)
                  }
                  dateEstablished={incomeData[id]?.dateEstablished || null}
                  setDateEstablished={(value) =>
                    updateIncomeData(id, "dateEstablished", value)
                  }
                  approximateStartDate={
                    incomeData[id]?.approximateStartDate || null
                  }
                  setApproximateStartDate={(value) =>
                    updateIncomeData(id, "approximateStartDate", value)
                  }
                  annualIncome={incomeData[id]?.annualIncome || ""}
                  setAnnualIncome={(value) =>
                    updateIncomeData(id, "annualIncome", value)
                  }
                  operatingType={incomeData[id]?.operatingType || ""}
                  setOperatingType={(value) =>
                    updateIncomeData(id, "operatingType", value)
                  }
                  jobTitle={incomeData[id]?.jobTitle || ""}
                  setJobTitle={(value) =>
                    updateIncomeData(id, "jobTitle", value)
                  }
                  annualBasePay={incomeData[id]?.annualBasePay || ""}
                  setAnnualBasePay={(value) =>
                    updateIncomeData(id, "annualBasePay", value)
                  }
                  businessNameError={errors.businessName}
                  dateEstablishedError={errors.dateEstablished}
                  approximateStartDateError={errors.approximateStartDate}
                  annualBasePayError={errors.annualBasePay}
                  operatingTypeError={errors.operatingType}
                  jobTitleError={errors.jobTitle}
                  annualIncomeError={errors.annualIncome}
                />
              </div>
            );
          case "rentalIncome":
            return (
              <div key={id} className="w-full">
                <RentalIncomeBox
                  componentId={id}
                  onRemove={() => removeIncome(id)}
                  address={incomeData[id]?.address || ""}
                  setAddress={(value) => updateIncomeData(id, "address", value)}
                  address2={incomeData[id]?.address2 || ""}
                  setAddress2={(value) =>
                    updateIncomeData(id, "address2", value)
                  }
                  city={incomeData[id]?.city || ""}
                  setCity={(value) => updateIncomeData(id, "city", value)}
                  postalCode={incomeData[id]?.postalCode || ""}
                  setPostalCode={(value) =>
                    updateIncomeData(id, "postalCode", value)
                  }
                  province={incomeData[id]?.province || ""}
                  setProvince={(value) =>
                    updateIncomeData(id, "province", value)
                  }
                  propertyType={incomeData[id]?.propertyType || ""}
                  setPropertyType={(value) =>
                    updateIncomeData(id, "propertyType", value)
                  }
                  propertyValue={incomeData[id]?.propertyValue || ""}
                  setPropertyValue={(value) =>
                    updateIncomeData(id, "propertyValue", value)
                  }
                  monthlyRent={incomeData[id]?.monthlyRent || ""}
                  setMonthlyRent={(value) =>
                    updateIncomeData(id, "monthlyRent", value)
                  }
                  rentalIncomeSince={incomeData[id]?.rentalIncomeSince || null}
                  setRentalIncomeSince={(value) =>
                    updateIncomeData(id, "rentalIncomeSince", value)
                  }
                  annualAmount={incomeData[id]?.annualAmount || ""}
                  setAnnualAmount={(value) =>
                    updateIncomeData(id, "annualAmount", value)
                  }
                  addressError={errors.address}
                  cityError={errors.city}
                  postalCodeError={errors.postalCode}
                  provinceError={errors.province}
                  propertyTypeError={errors.propertyType}
                  propertyValueError={errors.propertyValue}
                  monthlyRentError={errors.monthlyRent}
                  rentalIncomeSinceError={errors.rentalIncomeSince}
                  annualAmountError={errors.annualAmount}
                />
              </div>
            );
          case "otherIncome":
            return (
              <div key={id} className="w-full">
                <OtherIncomeBox
                  onRemove={() => removeIncome(id)}
                  incomeType={incomeData[id]?.incomeType || ""}
                  setIncomeType={(value) =>
                    updateIncomeData(id, "incomeType", value)
                  }
                  annualAmount={incomeData[id]?.annualAmount || ""}
                  setAnnualAmount={(value) =>
                    updateIncomeData(id, "annualAmount", value)
                  }
                  approximateStartDate={
                    incomeData[id]?.approximateStartDate || null
                  }
                  setApproximateStartDate={(value) =>
                    updateIncomeData(id, "approximateStartDate", value)
                  }
                  incomeTypeError={errors.incomeType}
                  annualAmountError={errors.annualAmount}
                  approximateStartDateError={errors.approximateStartDate}
                />
              </div>
            );
          default:
            return null;
        }
      })}

      {!showOptions && (
        <div
          onClick={() => setShowOptions(true)}
          onKeyDown={() => setShowOptions(true)}
          className="border border-[#E5E7EB] w-full rounded-[8px] flex items-center gap-2 font-medium p-5 cursor-pointer"
        >
          <img src={PlusIcon} alt="plus icon" />
          Add Another Income
        </div>
      )}

      {showOptions && (
        <div className="space-y-2 w-full">
          <div
            onClick={() => handleAddIncome("fullTime")}
            onKeyDown={() => handleAddIncome("fullTime")}
            className="border border-[#E5E7EB] w-full rounded-[8px] flex items-center gap-2 font-medium p-5 cursor-pointer"
          >
            <img src={PlusIcon} alt="plus icon" />I am Employed Full Time
          </div>
          <div
            onClick={() => handleAddIncome("partTime")}
            onKeyDown={() => handleAddIncome("partTime")}
            className="border border-[#E5E7EB] w-full rounded-[8px] flex items-center gap-2 font-medium p-5 cursor-pointer"
          >
            <img src={PlusIcon} alt="plus icon" />I am Employed Part Time
          </div>
          <div
            onClick={() => handleAddIncome("selfEmployed")}
            onKeyDown={() => handleAddIncome("selfEmployed")}
            className="border border-[#E5E7EB] w-full rounded-[8px] flex items-center gap-2 font-medium p-5 cursor-pointer"
          >
            <img src={PlusIcon} alt="plus icon" />I am Self-Employed
          </div>
          <div
            onClick={() => handleAddIncome("rentalIncome")}
            onKeyDown={() => handleAddIncome("rentalIncome")}
            className="border border-[#E5E7EB] w-full rounded-[8px] flex items-center gap-2 font-medium p-5 cursor-pointer"
          >
            <img src={PlusIcon} alt="plus icon" />I have Rental Income
          </div>
          <div
            onClick={() => handleAddIncome("otherIncome")}
            onKeyDown={() => handleAddIncome("otherIncome")}
            className="border border-[#E5E7EB] w-full rounded-[8px] flex items-center gap-2 font-medium p-5 cursor-pointer"
          >
            <img src={PlusIcon} alt="plus icon" />I have Other Income
          </div>
        </div>
      )}
    </div>
  );
};

export { EmploymentDetailsView };
